import {useState, useMemo} from "react";
import {Avatar, Box, Dialog, DialogContent, Grid, Typography} from "@mui/material";
import {Close, RefreshOutlined} from "@mui/icons-material";
import {CollapsibleGroupedSelect, StyledButtonContained, StyledButtonOutlined} from "../../utils/uiUtils";

/**
 * SelectUserDialog — picker dialog for choosing an existing user.
 *
 * Props:
 *   open      — boolean
 *   onClose   — () => void
 *   users     — { platform: [...], contacts: [...] }  OR flat array (backwards compat)
 *   onConfirm — (userObject) => void
 *   onRefresh — () => void  — optional, shows refresh button when provided
 */
export const SelectUserDialog = ({open, onClose, users, onConfirm, onRefresh}) => {
    // selection shape from CollapsibleGroupedSelect: { [groupKey]: [itemKey] }
    const [selection, setSelection] = useState({});

    // Normalise: accept both flat array and { platform, contacts }
    const platform = Array.isArray(users) ? users : (users?.platform || []);
    const contacts = Array.isArray(users) ? [] : (users?.contacts || []);

    const userKey = (u, i) => u.uuid || u.email || String(i);
    const userLabel = (u) => `${u.title ? u.title + ' ' : ''}${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Unknown';

    // Build groups for the component + a (groupKey -> itemKey -> user) lookup for mapping back.
    const {groups, lookup} = useMemo(() => {
        const grps = [];
        const lk = {};
        const addGroup = (key, label, list) => {
            if (!list.length) return;
            lk[key] = {};
            const items = list.map((u, i) => {
                const k = userKey(u, i);
                lk[key][k] = u;
                return {key: k, label: userLabel(u), _user: u};
            });
            grps.push({key, label, items});
        };
        addGroup('platform', 'Platform Users', platform);
        addGroup('contacts', 'Shipment Contacts', contacts);
        return {groups: grps, lookup: lk};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users]);

    const hasSelection = Object.values(selection).flat().length > 0;

    const handleClose = () => {
        setSelection({});
        onClose();
    };

    const handleConfirm = () => {
        const entries = Object.entries(selection);
        if (entries.length) {
            const [groupKey, itemKeys] = entries[0];
            const user = lookup[groupKey]?.[itemKeys[0]];
            if (user) onConfirm(user);
        }
        setSelection({});
    };

    const renderUserItem = (item, groupKey, isSelected) => {
        const u = item._user;
        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                <Avatar
                    src={u.profile_picture_url || u.profile_picture || ''}
                    sx={{width: 36, height: 36, fontSize: 14, bgcolor: 'rgba(255,197,64,0.15)', border: '1px solid rgba(255,197,64,0.4)'}}
                >
                    {`${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase() || '?'}
                </Avatar>
                <Box>
                    <Typography sx={{fontSize: 13, color: isSelected ? '#FFC540' : '#FFFFFF', fontFamily: 'Poppins'}}>
                        {userLabel(u)}
                    </Typography>
                    <Typography sx={{fontSize: 11, color: '#BABABA', fontFamily: 'Poppins'}}>
                        {[u.role, u.email].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Dialog open={open} fullWidth maxWidth="sm" PaperProps={{sx: {borderRadius: "12px", backgroundColor: '#3A3E3F'}}}>
            <DialogContent>
                <Grid container direction='column' spacing={2}>
                    <Grid item>
                        <Grid container justifyContent='space-between' alignItems='center'>
                            <Typography sx={{color: '#FFC540', fontWeight: 600, fontSize: '24px'}}>Select User</Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                {onRefresh && (
                                    <RefreshOutlined
                                        onClick={onRefresh}
                                        sx={{color: '#BABABA', cursor: 'pointer', '&:hover': {color: '#FFC540'}}}
                                        titleAccess="Refresh list"
                                    />
                                )}
                                <Close onClick={handleClose} sx={{color: 'white', cursor: 'pointer'}}/>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <CollapsibleGroupedSelect
                            title="Choose a user"
                            groups={groups}
                            value={selection}
                            onChange={setSelection}
                            noSelectionText=""
                            selectedDisplay="item-only"
                            renderItem={renderUserItem}
                            minWidth={'100%'}
                        />
                    </Grid>
                    <Grid item>
                        <Grid container justifyContent='flex-end' spacing={2}>
                            <Grid item>
                                <StyledButtonOutlined variant='outlined' onClick={handleClose}>Cancel</StyledButtonOutlined>
                            </Grid>
                            <Grid item>
                                <StyledButtonContained variant='contained' onClick={handleConfirm} disabled={!hasSelection}>
                                    Select
                                </StyledButtonContained>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};
